import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';

/**
 * Hydration-safe replacement for `useAuthStore()`.
 *
 * Zustand's `useAuthStore()` hook is built on React 18's
 * `useSyncExternalStore`, which schedules a forced sync re-render right after
 * hydration. When that happens while a lazy route's Suspense boundary is
 * still hydrating (the page chunk is loading), React abandons the prerendered
 * server HTML and client-renders the boundary (recoverable error #421).
 *
 * This hook returns the exact same store state but subscribes only after
 * mount: the initial render reads `getState()` (identical value), and
 * subsequent store updates flow through a normal `setState`. Store updates
 * are never lost — any change between render and effect is picked up by the
 * `setState` call inside the effect.
 *
 * Use this instead of `useAuthStore()` in components that render on
 * prerendered pages (the app shell and any prerendered route component).
 */
export function useAuthSnapshot() {
  const [state, setState] = useState(useAuthStore.getState);

  useEffect(() => {
    // Catch any update that happened between first render and this effect.
    // Zustand keeps the state object reference stable when nothing changed,
    // so this bails out of re-rendering in the common case.
    setState(useAuthStore.getState());
    return useAuthStore.subscribe(setState);
  }, []);

  return state;
}
